import React, { Suspense, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { wrapPromise } from "go-core";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import RenderLimitedText from "go-core/components/RenderLimitedText";
import { useWindowSize } from "go-core/components/useWindowSize";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig } from "go-list/list/services/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { organizationApi } from "../../../../../../../../services/Api/Organization/organizationApi";
import { UserWithInviteApi } from "../../../../../../../../services/Api/Organization/organizationTypes";
import { selectOrganization } from "../../../../../../../../services/organizations/selectors";
import { selectUser } from "../../../../../../../../services/users/selectors";
import GenerateInvitationLinkModal from "../components/GenerateInvitationLinkModal";
import InviteUserModalForm from "../components/InviteUserModalForm";
import OrganizationUserForm from "../components/OrganizationUserForm/OrganizationUserForm";

interface ListState {
	resource?: Record<string, any>;
	responseData?: UserWithInviteApi[] | undefined;
	updateUser: (item: UserWithInviteApi, items: UserWithInviteApi[]) => void;
	updateInvitation: (item: UserWithInviteApi, items: UserWithInviteApi[]) => void;
	setParams: (params: Record<string, any>) => void;
	mobileActions: MobileActionProps[];
}

const listName = "ORGANIZATION_USER";
const resourceType = "ORGANIZATION_USER";

const List = ({ resource, updateUser, updateInvitation, responseData, setParams, mobileActions }: ListState) => {
	const [items, setItems] = useState<UserWithInviteApi[]>([]);
	const user = useSelector(selectUser);
	const { t } = useTranslation();
	const confirmation = useConfirmation();
	const { addSuccessFlash } = useFlash();
	const organization = useSelector(selectOrganization);
	const segmentContextValue = React.useContext(SegmentContext);
	const [userToGenerateInvitationLink, setUserToGenerateInvitationLink] = useState<UserWithInviteApi | undefined>();
	const userRoles = useSelector(selectUser)?.roles;
	const isAdminOrOfficeOrSupport =
		userRoles?.includes("ADMIN") || userRoles?.includes("OFFICE") || userRoles?.includes("SUPPORT");

	useEffect(() => {
		if (responseData) {
			setItems(responseData);
		}
	}, [responseData]);

	if (!resource) return null;
	const data = resource.read();

	const onRemoveUser = async (item: UserWithInviteApi) => {
		await confirmation({
			title: t("confirmation.title", { ns: "lib" }),
			message: t("confirmation.users.remove_user.message", { ns: "lib" }),
		});
		await organizationApi.removeUserFromOrganization(item.item_id);
		addSuccessFlash(t("lib:common.flash.removed"));
	};

	const onRemoveInvitation = async (item: UserWithInviteApi) => {
		await confirmation({
			title: t("lib:go_form.modal_confirm.title"),
			message: t("modules.user.field.confirmation_remove_invitation.title"),
		});
		await organizationApi.removeUserInvitation(item.item_id);
		addSuccessFlash(t("lib:common.flash.removed"));
	};

	const onResendInvitation = async (item: UserWithInviteApi) => {
		await organizationApi.resendInvitation(item.item_id);
		addSuccessFlash(t("flash.users.invitation_resended"));
	};

	const onAcceptInvitation = async (item: UserWithInviteApi) => {
		await organizationApi.acceptInvitation(item.item_id);
		addSuccessFlash(t("flash.users.invitation_accepted"));
	};

	let config = {
		fields: [
			{
				id: "name",
				name: t("lib:common.word.name"),
				type: "text",
				render: (item: UserWithInviteApi) => (
					<RenderLimitedText
						minWidth={200}
						textStyleIsLink
						onClick={() => {
							item.type === "INVITE" ? updateInvitation(item, items) : updateUser(item, items);
						}}
					>
						{item.name}
					</RenderLimitedText>
				),
				renderExport: (item: UserWithInviteApi) => item.name,
			},
			{
				id: "email",
				name: t("lib:common.word.email"),
				type: "text",
			},
			{
				id: "applications",
				disableSorting: true,
				name: t("modules.user.field.applications.title"),
				render: (item: UserWithInviteApi) => {
					return item.applications !== undefined
						? item.applications.map((application) => application.application_type).join(", ")
						: undefined;
				},
			},
			{
				id: "type",
				disableSorting: true,
				name: t("lib:common.word.type"),
				render: (item: UserWithInviteApi) => {
					if (item.type === "INVITE") {
						return <>{t("modules.user.field.invitation.title")}</>;
					}
					return <>{t("modules.user.field.user.title")}</>;
				},
			},
			{
				id: "permissions",
				disableSorting: true,
				name: `${t("modules.user.field.permissions.title")}`,
				render: (item: UserWithInviteApi) => {
					return item.permissions !== undefined
						? item.permissions.map((permission) => t(`enums.users.permissions.${permission}`)).join(", ")
						: undefined;
				},
			},
		],
		actions: [
			{
				name: t("common.action.edit", { ns: "lib" }),
				click: (item: UserWithInviteApi) => updateInvitation(item, items),
				visible: (item: UserWithInviteApi) => item.type === "INVITE",
			},
			{
				name: t("common.action.edit", { ns: "lib" }),
				click: (item: UserWithInviteApi) => updateUser(item, items),
				visible: (item: UserWithInviteApi) => item.type === "USER",
			},
			{
				name: t("common.action.send_invitation"),
				click: (item: UserWithInviteApi) => onResendInvitation(item),
				visible: (item: UserWithInviteApi) => item.type === "INVITE",
			},
			{
				name: t("common.action.remove", { ns: "lib" }),
				click: (item: UserWithInviteApi) => onRemoveInvitation(item),
				visible: (item: UserWithInviteApi) => item.type === "INVITE",
				refreshListAfter: true,
			},
			{
				name: t("common.action.remove", { ns: "lib" }),
				click: (item: UserWithInviteApi) => onRemoveUser(item),
				visible: (item: UserWithInviteApi) => item.type === "USER",
				refreshListAfter: true,
			},
			{
				name: t("common.action.accept", { ns: "lib" }),
				click: (item: UserWithInviteApi) => onAcceptInvitation(item),
				visible: (item: UserWithInviteApi) => user?.admin && item.type === "INVITATION",
				refreshListAfter: true,
			},
			{
				name: t("modules.user.action.show_invitation.title"),
				visible: (item: UserWithInviteApi) => item.type === "INVITE" && isAdminOrOfficeOrSupport,
				click: (item: UserWithInviteApi) => setUserToGenerateInvitationLink(item),
			},
		],
		selectedColumns: ["name", "email", "applications", "permissions", "type"],
		segments: [
			{
				id: "all",
				slug: "all",
				name: t("common.word.all", { ns: "lib" }),
			},
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		fetch: async (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			params.include = "organization,user,permissions,applications";
			setParams(params);
			return organizationApi.getUsersWithInvitations(params, { cancelToken: sourceToken?.token });
		},
		exportConfig: {
			title: t("common.word.users", { ns: "lib" }),
			filename: t("common.word.users", { ns: "lib" }),
			organization: organization.name,
		},
		saveSegment: async (segment: SegmentType) => segmentContextValue.save(listName, resourceType, segment),
		numberOfStickyColumnsAtTheStart: 1,
		doesIdColumnRedirectToPreviewPage: true,
		doNotUseDefaultDateRangeInFilters: true,
	} as ListConfig;
	const fields = data.fields ? data.fields : [];
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...fields] : fields,
	};

	return (
		<>
			{userToGenerateInvitationLink && (
				<GenerateInvitationLinkModal
					user={userToGenerateInvitationLink}
					onHide={() => setUserToGenerateInvitationLink(undefined)}
				/>
			)}
			<ListData
				data={items}
				config={config}
				onFetch={(items: UserWithInviteApi[]) => setItems(items)}
				mobileActions={mobileActions}
			/>
		</>
	);
};

const OrganizationUsersListPage = () => {
	const { t } = useTranslation();
	const [resource, setResource] = useState<Record<string, any>>();
	const [showUserModal, setShowUserModal] = useState(false);
	const [showInviteModal, setShowInviteModal] = useState(false);
	const [params, setParams] = useState<Record<string, any>>({});
	const [responseData, setResponseData] = useState<UserWithInviteApi[] | undefined>(undefined);
	const [userId, setUserId] = useState<number | undefined>(undefined);
	const [invitation, setInvitation] = useState<number | undefined>(undefined);
	const segmentContextValue = React.useContext(SegmentContext);
	const isMobile = useWindowSize().isMobile;
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const { addFlash } = useFlash();

	useEffect(() => {
		handleChangeTabTitle(t("common.word.users", { ns: "lib" }));
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType)));
	}, []);

	const handleUpdate = async () => {
		setShowInviteModal(false);
		setShowUserModal(false);
		setUserId(undefined);
		setInvitation(undefined);
		try {
			const res = await organizationApi.getUsersWithInvitations(params);
			setResponseData(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const handleCloseInviteUserModal = () => {
		setShowInviteModal(false);
		setInvitation(undefined);
	};

	const handleCloseUpdateUserModal = () => {
		setUserId(undefined);
		setShowUserModal(false);
	};

	const onUpdateUser = (data: UserWithInviteApi) => {
		setUserId(data.item_id);
		setShowUserModal(true);
	};

	const onUpdateInvitation = (data: UserWithInviteApi) => {
		setInvitation(data.item_id);
		setShowInviteModal(true);
	};

	const buttons: ButtonProps[] = [
		{
			title: t("common.action.invite_user"),
			variant: "primary",
			action: () => setShowInviteModal(true),
		},
	];
	const mobileActions: MobileActionProps[] = [
		{
			title: t("common.action.invite_user"),
			action: () => setShowInviteModal(true),
		},
	];

	return (
		<>
			{!isMobile && <Header title={t("common.word.users", { ns: "lib" })} buttons={buttons} />}
			{showInviteModal && (
				<InviteUserModalForm
					invitationId={invitation}
					handleClose={handleCloseInviteUserModal}
					handleSave={handleUpdate}
				/>
			)}
			{showUserModal && (
				<OrganizationUserForm
					organizationUserId={userId}
					handleClose={handleCloseUpdateUserModal}
					handleSave={handleUpdate}
				/>
			)}
			<Suspense fallback={<LoadingContainer />}>
				<List
					resource={resource}
					responseData={responseData}
					updateInvitation={onUpdateInvitation}
					updateUser={onUpdateUser}
					mobileActions={mobileActions}
					setParams={setParams}
				/>
			</Suspense>
		</>
	);
};

export default OrganizationUsersListPage;

// t("enums.users.permissions.DEFAULT");
// t("enums.users.permissions.USERS_MANAGMENT");
